import { PropsWithChildren, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { PATH_LOGIN, PATH_ONBOARDING } from "@routes/paths";
import { useAppDispatch, useAppSelector } from "@/hooks/redux-hooks";
import { initializeApp, getProfile } from "@store/reducers/auth.reducer";
import { getAllRoles } from "@store/reducers/roles.reducer";
import Loader from "@/components/Loader";

type Props = PropsWithChildren;

const AuthGuard: React.FC<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { initialized, user, isLoggedIn } = useAppSelector((s) => s.auth);
  const { initialized: rolesInitialized } = useAppSelector((s) => s.roles);
  const { pathname, search } = useLocation();
  const isOnboardingPage = pathname.startsWith(PATH_ONBOARDING);

  useEffect(() => {
    if (!initialized) dispatch(initializeApp());
    if (isLoggedIn && !user.id) dispatch(getProfile());
  }, [dispatch, initialized, isLoggedIn, user.id]);

  useEffect(() => {
    // Load the roles catalog as soon as we have a logged-in user so permission
    // checks (hasPermission) resolve correctly across the app.
    if (isLoggedIn && user.id && !rolesInitialized) {
      dispatch(getAllRoles());
    }
  }, [dispatch, isLoggedIn, user.id, rolesInitialized]);

  // Request permission to show notifications
  useEffect(() => {
    if (isLoggedIn && user.id && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, [isLoggedIn, user.id]);

  if (!isLoggedIn) {
    // Don't redirect if already on login page
    if (pathname !== PATH_LOGIN) {
      // Build the full path with search params if they exist
      const fullPath = pathname + search;
      // Encode the full path as a query parameter
      const loginUrl = `${PATH_LOGIN}?redirect=${encodeURIComponent(fullPath)}`;
      return <Navigate to={loginUrl} replace />;
    }
  }

  if (isLoggedIn && !user.id) return <Loader />;
  // Wait for the roles catalog before rendering protected content so that
  // permission-guarded routes/UI don't flash 403 during bootstrap.
  if (isLoggedIn && user.id && !rolesInitialized) return <Loader />;

  if (isLoggedIn && user?.onboarding && !isOnboardingPage) return <Navigate to={PATH_ONBOARDING} />;

  return <>{children}</>;
};

export default AuthGuard;
